import React from 'react';
import { Button, ButtonGroup, Intent, MenuItem } from '@blueprintjs/core';
import { Select } from '@blueprintjs/select';
import { FlexRow, PaddingWrapper } from 'components/styled-components';
import styled from 'styled-components';
import { IconNames } from '@blueprintjs/icons';
import range from 'lodash/range';
import find from 'lodash/find';

const PAGES_LIST = 9;
const PAGE_GREATER = 6;
const PAGE_LESSER = 4;
const PAGINATION_CHOICES = [
  { label: '10 Per Page', value: 10 },
  { label: '25 Per Page', value: 25 },
  { label: '50 Per Page', value: 50 },
];

const isActive = (page, index) => Number(page) === Number(index);
const calcPagesNeeded = (numRecords, perPage) =>
  Math.ceil(numRecords / perPage);

const generatePaginationButtons = (
  numRecords,
  perPage,
  currentPage,
  onPageClick
) => {
  const totalPages = calcPagesNeeded(numRecords, perPage);
  let startPage, endPage;
  //less than 10 pages, show them all
  if (totalPages <= 10) {
    startPage = 1;
    endPage = totalPages;
  } else {
    //more than 10, calculate the start and end pages to show
    if (currentPage <= PAGE_LESSER) {
      startPage = 1;

      endPage = currentPage + PAGE_GREATER;
    } else if (currentPage + PAGE_LESSER >= totalPages) {
      startPage = totalPages - PAGES_LIST;
      endPage = totalPages;
    } else {
      startPage = currentPage - PAGE_LESSER;
      endPage = currentPage + PAGE_GREATER;
    }
  }

  numRecords = range(startPage, endPage + 1);

  const pages = numRecords.map(number => {
    return (
      <Button
        onClick={() => onPageClick(number - 1)}
        key={number - 1}
        intent={
          isActive(currentPage, number - 1) ? Intent.PRIMARY : Intent.NONE
        }
      >
        {number}
      </Button>
    );
  });

  return pages;
};

const getRangeLabel = (currentPage, perPage, length) => {
  if (length === 0 || perPage === 0) {
    return `0 of ${length}`;
  }

  length = Math.max(length, 0);

  const startIndex = currentPage * perPage;

  // If the start index exceeds the list length, do not try and fix the end index to the end.
  const endIndex =
    startIndex < length
      ? Math.min(startIndex + perPage, length)
      : startIndex + perPage;

  return `${startIndex + 1} - ${endIndex} of ${length}`;
};

const renderPaginationChoices = (
  selection,
  { handleClick, modifiers, index }
) => {
  return (
    <MenuItem
      key={index}
      active={modifiers.active}
      onClick={handleClick}
      text={selection.label}
    />
  );
};

const Pagination = ({
  numRecords,
  perPage,
  currentPage,
  onPageClick,
  handlePerPageChange,
  hidePerPage,
}) => {
  return (
    <PaddingWrapper>
      {numRecords && numRecords > 0 ? (
        <FlexRow>
          {!hidePerPage && (
            <div>
              <Select
                items={PAGINATION_CHOICES}
                itemRenderer={renderPaginationChoices}
                filterable={false}
                onItemSelect={handlePerPageChange}
              >
                <Button
                  text={find(PAGINATION_CHOICES, ['value', perPage]).label}
                  rightIcon="caret-down"
                  className="light-blue oversize-child"
                  fill={true}
                  alignText="left"
                />
              </Select>

              <span style={{ marginLeft: '4rem' }}>
                {getRangeLabel(currentPage, perPage, numRecords)}
              </span>
            </div>
          )}

          <PagesContainer>
            <ButtonGroup>
              <Button
                icon={IconNames.DOUBLE_CHEVRON_LEFT}
                onClick={() => onPageClick(0)}
                disabled={currentPage === 0}
              />
              <Button
                icon={IconNames.CHEVRON_LEFT}
                onClick={() => onPageClick(currentPage - 1)}
                disabled={currentPage === 0}
              />
              {generatePaginationButtons(
                numRecords,
                perPage,
                currentPage,
                onPageClick
              )}
              <Button
                icon={IconNames.CHEVRON_RIGHT}
                onClick={() => onPageClick(currentPage + 1)}
                disabled={
                  currentPage + 1 === calcPagesNeeded(numRecords, perPage)
                }
              />
              <Button
                icon={IconNames.DOUBLE_CHEVRON_RIGHT}
                onClick={() =>
                  onPageClick(calcPagesNeeded(numRecords, perPage) - 1)
                }
                disabled={
                  currentPage + 1 === calcPagesNeeded(numRecords, perPage)
                }
              />
            </ButtonGroup>
          </PagesContainer>
        </FlexRow>
      ) : null}
    </PaddingWrapper>
  );
};

const PagesContainer = styled.div``;

export default Pagination;
